/**
 * Story 11.3: the proposed-vs-current rent math every renewal board card
 * (the $/% change) and the board's own header roll-up (R-N6, the projected
 * annualized rent change) both need — one shared, tested place, rather than
 * each computing it independently (`app/api/renewals.py::RenewalResource`'s
 * own doc comment: "computed by the frontend... never a third, persisted
 * field").
 */

/** `proposed - current`, in cents — the raw monthly $ change a card shows. */
export function monthlyRentChangeCents(currentRentCents: number, proposedRentCents: number): number {
  return proposedRentCents - currentRentCents;
}

/**
 * `(proposed - current) * 12` — R-N6's own literal formula for one
 * renewal's contribution to the portfolio-wide projected annualized change.
 */
export function annualizedRentChangeCents(currentRentCents: number, proposedRentCents: number): number {
  return monthlyRentChangeCents(currentRentCents, proposedRentCents) * 12;
}

/**
 * The percent change a card shows beside the $ change, or `null` when
 * `currentRentCents` is `0` — a real, if unusual, value this codebase never
 * divides by (mirrors every other "guard the divide, never show `Infinity`
 * or `NaN`" precedent this app already follows for a computed ratio).
 */
export function rentChangePercent(currentRentCents: number, proposedRentCents: number): number | null {
  if (currentRentCents === 0) {
    return null;
  }
  return (monthlyRentChangeCents(currentRentCents, proposedRentCents) / currentRentCents) * 100;
}

/** `12.5` -> `"+12.5%"`; `-4` -> `"-4.0%"`. Always signed, one decimal. */
export function formatRentChangePercent(percent: number): string {
  const sign = percent > 0 ? "+" : "";
  return `${sign}${percent.toFixed(1)}%`;
}
